import React, { useCallback, useMemo, useState } from 'react';
import { Box, CircularProgress, IconButton, Stack, Typography } from '@mui/material';
import { Cancel, Check } from '@mui/icons-material';
import { css } from '@emotion/react';
import { Controller, useWatch } from 'react-hook-form';
import {
    Alert,
    DiscardChangesDialog,
    FormWrapper,
    TextField,
    useDiscardChangesGuard,
} from 'c-components';
import type { FormWrapperProps } from 'c-components/Forms/FormWrapper/FormWrapper';
import type { ScheduleFormData } from 'c-main/Components/Schedule/types';
import { emptyFormDataRule } from 'c-main/Components/Schedule/templates';
import { useAPIClientRequest } from 'c-hooks';
import apiClient from 'c-data/apiClient';
import { scheduleName, scheduleRuleToFormRuleData } from 'c-main/Components/Schedule/lib';
import { useIsDspSchedule } from 'c-main/Components/Schedule/Dsp/ScheduleVariantContext';
import to from 'await-to-js';
import { useHotkeys } from 'react-hotkeys-hook';
import type { Campaign, Schedule, ScheduleGet } from 'c-sdk';
import { useCommonTranslation } from 'c-translation';
import { useEntityData } from 'c-data';
import type { DateRangePickerProps } from '@mui/x-date-pickers-pro';
import RuleFormList from './RuleFormList';
import type { CampaignDateBounds } from './scheduleFormLib';
import {
    campaignDateBounds,
    campaignDateRange,
    campaignDurationShortcutRange,
    ruleDateRangeError,
    scheduleFormRulesPayload,
} from './scheduleFormLib';

type FormProps = {
    onClose: () => void;
    isLoading: boolean;
    error?: string;

    hideDates?: boolean;
    dateRangeSlotProps: DateRangePickerProps<any>['slotProps'];
    campaignDates: CampaignDateBounds;
};
const ScheduleForm: React.FC<FormProps> = ({
    onClose,
    isLoading,
    error,
    dateRangeSlotProps,
    hideDates,
    campaignDates,
}) => {
    const t = useCommonTranslation();
    // UL-8164 -- no saving while a rule's dates are outside the campaign (or end before they start).
    const rules = useWatch<ScheduleFormData, 'rules'>({ name: 'rules' });
    const hasDateError = useMemo(
        () =>
            !hideDates &&
            (rules ?? []).some(rule => ruleDateRangeError(rule?.date_range, campaignDates) != null),
        [rules, hideDates, campaignDates],
    );
    return (
        <Box display="flex" overflow="hidden" flex={1} pt={2} flexDirection="column">
            <Stack gap={2} flex={1} overflow="auto">
                <Controller
                    render={({ field }) => (
                        <TextField
                            {...field}
                            label={t(
                                'Modules.Main.Campaigns.CampaignSchedule.Form.scheduleNameLabel',
                            )}
                            variant="standard"
                            size="small"
                        />
                    )}
                    name="name"
                />
                <RuleFormList
                    dateRangeSlotProps={dateRangeSlotProps}
                    hideDates={hideDates}
                    campaignDates={campaignDates}
                />
            </Stack>
            <Box mt="auto" minHeight={40}>
                {error && (
                    <Box mt={2}>
                        <Alert variant="outlined" severity="error">
                            {String(error)}
                        </Alert>
                    </Box>
                )}
                <Box ml="auto" textAlign="right">
                    <IconButton
                        color="error"
                        onClick={onClose}
                        disabled={isLoading}
                        aria-label={t('Dialog.defaultCancelLabel')}
                    >
                        <Cancel />
                    </IconButton>
                    <IconButton color="primary" type="submit" disabled={isLoading || hasDateError}>
                        {isLoading ? <CircularProgress size={20} /> : <Check />}
                    </IconButton>
                </Box>
            </Box>
        </Box>
    );
};

type Props = {
    onClose: () => void;
    campaignId?: number;
    schedule?: ScheduleGet;
    updateSchedules: (schedules: ScheduleGet[] | Schedule) => void;
    hideDates?: boolean;
    template?: boolean;
};

const ScheduleFormWrapper: React.FC<Props> = ({
    campaignId,
    onClose,
    schedule,
    updateSchedules,
    hideDates = false,
    template = false,
}) => {
    // UL-8114 -- an edited form asks before Escape or Cancel throws the edit away.
    const [isDirty, setIsDirty] = useState(false);
    const { requestClose, confirmProps } = useDiscardChangesGuard(isDirty, onClose);
    const confirming = confirmProps.open;
    // While asking, Escape belongs to the confirm ("Keep editing"), not to the form.
    const onEscape = useCallback(() => {
        if (!confirming) requestClose();
    }, [confirming, requestClose]);
    useHotkeys('escape', onEscape, [onEscape]);

    const {
        start: createSchedule,
        isLoading: isCreating,
        error: createError,
    } = useAPIClientRequest(apiClient.Entities.Schedule.create);
    const {
        start: updateSchedule,
        isLoading: isUpdating,
        error: updateError,
    } = useAPIClientRequest(apiClient.Entities.Schedule.update);

    const isDsp = useIsDspSchedule();

    const onCreate = useCallback(
        async (data: ScheduleFormData) => {
            const [err, succ] = await to(
                createSchedule({
                    campaign_id: campaignId,
                    name: data.name ?? '',
                    // UL-7912 -- on a DSP campaign SOV and slot length are cleared on every save.
                    rules: scheduleFormRulesPayload(data.rules, isDsp),
                    includes: ['schedulables', 'rules'],
                    template,
                }),
            );

            if (!err && succ?.data?.data?.id != null) {
                updateSchedules([succ.data.data]);
                onClose();
            }
        },
        [isDsp, createSchedule, updateSchedules, campaignId, onClose, template],
    );
    const onUpdate = useCallback(
        async (data: ScheduleFormData) => {
            const [err, succ] = await to(
                updateSchedule({
                    schedule_id: schedule?.id,
                    campaign_id: campaignId,
                    name: data.name ?? '',
                    rules: scheduleFormRulesPayload(data.rules, isDsp),
                    includes: ['schedulables', 'rules'],
                }),
            );

            if (!err && succ?.data?.data?.id != null) {
                updateSchedules([succ.data.data]);
                onClose();
            }
        },
        [isDsp, updateSchedule, schedule?.id, campaignId, updateSchedules, onClose],
    );

    const { getById } = useEntityData<Campaign>('Campaign');
    const campaign = useMemo(() => getById({ id: campaignId }), [getById, campaignId]);
    const campaignDates = useMemo(
        () => campaignDateBounds(campaign?.date_start, campaign?.date_end),
        [campaign?.date_start, campaign?.date_end],
    );
    const formOpts = useMemo<FormWrapperProps<ScheduleFormData>['formOptions']>(
        () => ({
            defaultValues: {
                name: schedule?.name ?? '',
                rules: schedule
                    ? schedule.rules.map(rule => ({
                          ...scheduleRuleToFormRuleData(rule),
                          id: rule.id ?? undefined,
                      }))
                    : [emptyFormDataRule(hideDates ? undefined : campaignDateRange(campaignDates))],
            },
        }),
        // A new rule starts on the campaign's dates, so it fills in if the campaign loads late.
        [schedule, hideDates, campaignDates],
    );
    const t = useCommonTranslation();
    const header = useMemo(
        () => (
            <Typography variant="subtitle2" color="primary">
                {!schedule
                    ? t('Modules.Main.Campaigns.CampaignSchedule.Form.newSchedule')
                    : t('Modules.Main.Campaigns.CampaignSchedule.Form.updateSchedule', {
                          name: scheduleName(schedule, t),
                      })}
            </Typography>
        ),
        [schedule, t],
    );

    const isLoading = useMemo(
        () => (schedule ? isUpdating : isCreating),
        [schedule, isUpdating, isCreating],
    );

    const error = useMemo(
        () => (schedule ? updateError : createError),
        [schedule, updateError, createError],
    );

    const dateRangeSlotProps = useMemo<DateRangePickerProps<Date>['slotProps']>(
        () => ({
            shortcuts: {
                items: [
                    {
                        label: t(
                            'Modules.Main.Campaigns.CampaignSchedule.Form.campaignDurationShortcut',
                        ),
                        getValue: () =>
                            campaignDurationShortcutRange(campaign?.date_start, campaign?.date_end),
                    },
                ],
            },
        }),
        [campaign, t],
    );

    const formComponentChildren = useMemo(
        () => (
            <Box flex={1} display="flex" flexDirection="column" overflow="hidden" width="100%">
                {header}
                <ScheduleForm
                    onClose={requestClose}
                    isLoading={isLoading}
                    error={error}
                    dateRangeSlotProps={dateRangeSlotProps}
                    hideDates={hideDates}
                    campaignDates={campaignDates}
                />
            </Box>
        ),
        [header, requestClose, isLoading, error, dateRangeSlotProps, hideDates, campaignDates],
    );

    const onSubmit = useMemo(() => {
        if (schedule == null) return onCreate;
        return onUpdate;
    }, [schedule, onCreate, onUpdate]);

    return (
        <Box flex={1} display="flex" overflow="hidden">
            <FormWrapper<ScheduleFormData>
                onSubmit={onSubmit}
                formOptions={formOpts}
                onDirtyChange={setIsDirty}
                formProps={useMemo<any>(
                    () => ({ css: css('width:100%;display:flex;position:relative;') }),
                    [],
                )}
            >
                {formComponentChildren}
            </FormWrapper>
            <DiscardChangesDialog {...confirmProps} />
        </Box>
    );
};

export default ScheduleFormWrapper;
